// Voice Integration — the ONLY place that knows the backend's practice-facing
// voice-connection routes, the feature key that gates them, and the vocabulary
// of the rows they return. Spec: Voice-Integration-Spec.md, "Part B addendum:
// practice-facing API for the Integrations page". If the backend lands with a
// different path or field name, change it here and in the tests; nothing else
// should hard-code a string from this contract.

// Access rule (dentalcrm #6794, IsVoicePracticeAdmin): superuser or an enabled
// practice user with role ADMIN. There is no feature flag; the card mirrors the
// role check and the thunks do not gate on practice feature permissions.
import { parseApiDate } from '../../callLogs/callLog.normalize';
import { fDateTime } from '../../../../utils/formatTime';

// Backend datetimes arrive as "DD-MM-YYYY HH:MM:SS.ffffffZ" (DRF DATETIME_FORMAT),
// which `new Date()` reads month-first ("11-10-2026" became 10 Nov on the
// staging smoke). Route every timestamp through the Call Logs parser.
export const fVoiceDateTime = (value) => fDateTime(parseApiDate(value));

// The ingest URL is built server-side with request.build_absolute_uri, which
// reports http behind the platform proxy. The API is only ever served over
// TLS, so never hand a vendor an http:// webhook.
export const httpsWebhookUrl = (url) => (typeof url === 'string' ? url.replace(/^http:\/\//i, 'https://') : url);

export const voiceConnectionsBase = (practiceId) => `/api/v1/practices/${practiceId}/voice-connections/`;

export const VOICE_URLS = {
    list: (practiceId) => voiceConnectionsBase(practiceId),
    providers: (practiceId) => `${voiceConnectionsBase(practiceId)}providers/`,
    detail: (practiceId, id) => `${voiceConnectionsBase(practiceId)}${id}/`,
    rotateSecret: (practiceId, id) => `${voiceConnectionsBase(practiceId)}${id}/rotate-secret/`,
    disconnect: (practiceId, id) => `${voiceConnectionsBase(practiceId)}${id}/disconnect/`,
    // Subscription providers (Elevate): swap credentials on a disconnected connection.
    reconnect: (practiceId, id) => `${voiceConnectionsBase(practiceId)}${id}/reconnect/`,
    status: (practiceId, id, hours = 24) => `${voiceConnectionsBase(practiceId)}${id}/status/?hours=${hours}`,
    // GET/PUT { enabled } -> { enabled, can_change }: transcripts and summaries for
    // answered calls. Metered work, so only DentalSEM may change it.
    transcription: (p, id) => `${voiceConnectionsBase(p)}${id}/transcription/`,
    testEvent: (practiceId, id) => `${voiceConnectionsBase(practiceId)}${id}/test-event/`,
    userMapping: (practiceId, id) => `${voiceConnectionsBase(practiceId)}${id}/user-mapping/`,
};

// `status` on a list row. Labels/colours for the chip on the card.
export const CONNECTION_STATUS = {
    healthy: { label: 'Healthy', color: 'success' },
    no_events_yet: { label: 'Waiting for first event', color: 'warning' },
    rejections: { label: 'Rejected events', color: 'error' },
    disabled: { label: 'Disconnected', color: 'default' },
};

export const statusMeta = (status) => CONNECTION_STATUS[status] || { label: status || 'Unknown', color: 'default' };

// Providers come in two auth modes (dentalcrm #6889):
//  - signed-push: the vendor POSTs HMAC-signed events; we hand them a secret.
//  - subscription: WE subscribe to the vendor's webhook API with the practice's
//    client id/secret (Intermedia Elevate); the vendor calls back with basic auth
//    and there is never a secret to reveal.
export const providerAuthMode = (providers, key) => providers.find((p) => p.key === key)?.auth_mode || 'signed-push';
export const isSubscriptionProvider = (provider) => provider?.auth_mode === 'subscription';

// Whether this phone system lets the CRM control a handset (place, end, transfer).
// Desks only mean anything where it does: on Twilio or WhatsApp Calling there are no
// desks at all, so the step is absent rather than stuck at "0 of 21 connected". Falls
// back to the auth mode when `providers/` has not answered, which is how this was read
// before capabilities were reported.
export const providerCanControlHandsets = (providers, key) => {
    const row = (providers || []).find((p) => p.key === key);
    if (!row) return false;
    if (row.supports && typeof row.supports.click_to_dial === 'boolean') return row.supports.click_to_dial;
    return row.auth_mode === 'subscription';
};

// `subscription_state` on a subscription-provider row.
export const SUBSCRIPTION_STATUS = {
    inactive: { label: 'Not subscribed', color: 'default' },
    active: { label: 'Subscribed', color: 'success' },
    suspended: { label: 'Subscription suspended', color: 'warning' },
    error: { label: 'Subscription error', color: 'error' },
};
export const subscriptionMeta = (state) => SUBSCRIPTION_STATUS[state] || { label: state || 'Unknown', color: 'default' };

// What the connect dialog offers when `providers/` has not answered (or 404s on
// an older backend). Keys match the spec's provider enum.
export const FALLBACK_PROVIDERS = [
    {
        key: 'nec_univerge_blue',
        label: 'On Hold Comms / NEC UNIVERGE BLUE',
        auth_mode: 'signed-push',
        supports: { push_events: true, pull_history: false, click_to_dial: false, recordings: true },
        setup_notes: 'Ask On Hold Comms to point the platform\'s call-event webhook at the URL below and sign requests with the secret.',
    },
];

// Normalises a vendor-supplied phone number to the E.164 form the backend
// expects for `site_numbers`. Accepts "07852 461505", "+44 7852 461505" and
// "447852461505"; returns null when nothing usable remains.
export const toE164 = (raw) => {
    const digits = String(raw || '').replace(/[^\d+]/g, '');
    if (!digits) return null;
    if (digits.startsWith('+')) return digits.length > 3 ? digits : null;
    if (digits.startsWith('00')) return `+${digits.slice(2)}`;
    if (digits.startsWith('0')) return `+44${digits.slice(1)}`;
    return `+${digits}`;
};

export const parseSiteNumbers = (text) => Array.from(new Set(
    String(text || '').split(/[\n,;]+/).map(toE164).filter(Boolean)
));
